import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type HistoryEntry, type QrPayload, type User } from '../api';
import { StampCard } from '../components/StampCard';
import { WalletButtons } from '../components/WalletButtons';
import { Logo } from '../components/BrandMarks';
import { useT } from '../i18n';

interface Props {
  user: User;
  navigate: (path: string) => void;
  onLogout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export function Profile({ user, navigate, onLogout, refreshUser }: Props) {
  const t = useT();
  const [qr, setQr] = useState<QrPayload | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const timerRef = useRef<number | null>(null);

  const loadQr = useCallback(async () => {
    try {
      const next = await api.myQr();
      setQr(next);
      setQrError(null);
    } catch {
      setQrError(t.profile.qrFailed);
    }
  }, []);

  // Re-issue shortly before the current code lapses, so the QR on screen is
  // always live and a screenshot stops working within a couple of minutes.
  useEffect(() => {
    void loadQr();
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [loadQr]);

  useEffect(() => {
    if (!qr) return;
    const lead = Math.max(5_000, qr.ttlMs - 10_000);
    timerRef.current = window.setTimeout(() => void loadQr(), lead);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [qr, loadQr]);

  // The staff can stamp this card at any moment, so poll for the new count
  // while the profile is the visible screen.
  const loadHistory = useCallback(async () => {
    try {
      const res = await api.myHistory();
      setHistory(res.history);
    } catch {
      // The card above already shows the balance; the log is supplementary.
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void refreshUser();
      void loadHistory();
    }, 10_000);
    return () => window.clearInterval(id);
  }, [refreshUser, loadHistory]);

  return (
    <div className="panel panel--profile screen">
      <div className="panel-head">
        <Logo title="Caffeina" />
        <button type="button" className="btn--quiet" onClick={() => void onLogout()}>
          {t.profile.logout}
        </button>
      </div>

      <div className="eyebrow eyebrow--tight" style={{ marginBottom: 6 }}>
        {t.profile.hello}
      </div>
      <h1 className="greeting-name">{user.firstName}</h1>

      {/* On a laptop this becomes two columns: the QR beside everything else. */}
      <div className="profile-grid">
        <div className="card card--qr">
          <div className="eyebrow eyebrow--tight" style={{ marginBottom: 14 }}>
            {t.profile.yourCode}
          </div>
          {qr ? (
            <img className="qr-image" src={qr.image} alt={t.profile.qrAlt(user.name)} />
          ) : (
            <div
              className="qr-image"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {qrError ? (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{qrError}</span>
              ) : (
                <span className="spinner" />
              )}
            </div>
          )}
          <div className="qr-caption">
            {t.profile.qrCaption}
          </div>
        </div>

        <div>
          <WalletButtons />

          <StampCard stamps={user.stamps} rewards={user.rewards} />

          <div className="reward-list">
            {user.rewards.map((reward) => (
              <div className="reward" key={reward.id}>
                <div>
                  <div className="reward-name">{reward.name}</div>
                  <div className="reward-detail">{reward.detail}</div>
                </div>
                <div
                  className={
                    reward.available ? 'reward-status reward-status--ready' : 'reward-status'
                  }
                >
                  {reward.available
                    ? t.profile.available
                    : t.profile.remaining(reward.remaining)}
                </div>
              </div>
            ))}
          </div>

          {history.length > 0 && (
            <>
              <div className="eyebrow eyebrow--tight" style={{ marginBottom: 10 }}>
                {t.profile.activity}
              </div>
              <div style={{ marginBottom: 28 }}>
                {history.map((entry) => (
                  <div className="tx-row" key={entry.id}>
                    <div className="tx-label">
                      {entry.kind === 'stamp'
                        ? t.profile.stampAdded(entry.stampsAfter)
                        : t.profile.rewardRedeemed(entry.reward ?? t.profile.rewardFallback)}
                    </div>
                    <div className="tx-time">{entry.when}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {user.role === 'staff' && (
        <div className="staff-link">
          <button type="button" onClick={() => navigate('/admin')}>
            {t.profile.staffAccess}
          </button>
        </div>
      )}
    </div>
  );
}
